import { markRaw } from 'vue'
import { Loading, LocalStorage } from 'quasar'
import initHelpHero, { ChecklistEvent, ChecklistEventInfo } from 'helphero'
import { UserDto, UserStatsDto } from 'booksprout'

import { BsSpinner } from '../components/bs-component-bundle'
import authStoreGetters from '../modules/authentication/store/getters'
import { authenticationService } from '../modules/authentication/authenticationService'
import { AuthenticatedUser } from '../modules/_base/auth/auth.service.interface'
import { useAxios } from '../modules/axios/axiosModule'
import { useMessageThread } from '../modules/messages/messageThreadModule'
import { useNotification } from '../modules/notifications/notificationModule'
import { useUi } from '../modules/ui/uiModule'
import { useUser } from '../modules/user/userModule'
import { useAppShell } from './useAppShell'
import { APP_ICONS } from './appIcons'

/**
 * What the root component of all four apps actually *does*, as opposed to what it decides.
 *
 * `useAppShell` holds the judgement — who gets a tour, whether polling is worth doing, which errors are
 * worth escalating. Its docblock said the polling itself would stay on `BaseApp` "until stage 8 turns
 * this component into a `setup()`, where it can move here with its dependencies rather than through eight
 * context arguments". This is that move: the four `App.ts` files are converting, so the orchestration
 * comes here and takes its dependencies from the modules directly.
 *
 * `BaseApp` keeps every member and delegates, so nothing that still extends it changes.
 *
 * ── what is NOT here ──────────────────────────────────────────────────────────────────────────────
 *
 * The ui-store watchers each `App.ts` declares — the redirect, the message dialog, the login prompt, the
 * auth-expiry dialog. Those differ per app (the reviewer app has twice as many) and they are already
 * per-app code rather than inherited, so converting each `App.ts` moves them as they are.
 */

export interface AppRuntimeHost {
  $q: {
    dialog (opts: any): { onOk (cb: () => void): unknown }
    iconMapFn: unknown
    iconSet: { datetime: { arrowLeft: string, arrowRight: string } }
  }
  $route: { query?: Record<string, unknown> }
  $router: { push (to: string): Promise<unknown> }
  $bs: { confirmDialog: unknown }
}

export interface AppRuntimeContext {
  host: AppRuntimeHost
  /** Read fresh: the poll runs once a minute and the session changes underneath it. */
  mimicOrAuthenticatedUser: () => AuthenticatedUser
  authenticatedUser: () => AuthenticatedUser
  isMobileApp: () => boolean
  isReviewerApp: () => boolean
  /**
   * Overridable, because `shouldSendHelpHeroData` is public on `BaseApp` and the mixin has to stay the
   * one that answers for anything still extending it.
   */
  shouldSendHelpHeroData?: (stats: UserStatsDto) => boolean
}

/**
 * Set by `startHelpHero`, read by the identify call and the teardown. Module-scoped, as it was on BaseApp.
 *
 * BUG (noted, P3, pre-existing and NOT asserted): the identify call in `intervalRoutines` guards on
 * `isHelpHeroEnabled` but not on this being set, and a caller polls before it starts HelpHero —
 * `BaseApp.mounted` did, and a converted `App.ts` does the same. It survives because the first `getById`
 * resolves a microtask later, by which time mounting has finished; a slower mount, or a caller that polls
 * without starting HelpHero at all, throws `HelpHero.identify is not a function` into the rethrow below.
 *
 * Left as it is, and unasserted: being module-scoped, it stays set for the life of the module once any
 * caller has started it, so no spec in this file can observe the unset state without resetting the module
 * around itself. Recorded here instead.
 */
let HelpHero: any = void 0

export function useAppRuntime (context: AppRuntimeContext) {
  const { host } = context
  const shell = useAppShell()

  // markRaw'd because Vue's observer has no business walking a service — the axios one holds an
  // axios instance, which it would otherwise walk in full.
  const messageThreadService = markRaw(useMessageThread().messageThread.service)
  const authService = markRaw(authenticationService())
  const userService = markRaw(useUser().user.service)
  const notificationService = markRaw(useNotification().notification.service)
  const axiosService = markRaw(useAxios())

  let loggedInUserActionsInterval: any = void 0

  const shouldSendHelpHeroData = (stats: UserStatsDto) => {
    return shell.shouldSendHelpHeroData(
      stats,
      context.mimicOrAuthenticatedUser(),
      context.isReviewerApp()
    )
  }

  /**
   * Once a minute, for a signed-in visitor: re-read the account, refresh the unread counts and the stats,
   * hand the stats to HelpHero if this visitor is being toured, and ask the server whether it has been
   * deployed underneath them.
   *
   * Every service has its global loader switched off for the duration — this is background polling, and a
   * spinner over the whole app once a minute would be unusable — and switched back on in `finally`.
   *
   * Both chains are RETURNED, where the mixin discarded them — separately, not combined, so that whichever
   * one rejects still rejects on its own and an unhandled rejection stays exactly as unhandled as it was
   * for a caller that ignores them (BaseApp does). Handing them back is what lets a caller, or a spec,
   * observe the two rethrows below rather than race Node's unhandled-rejection hook.
   */
  const intervalRoutines = () => {
    if (context.mimicOrAuthenticatedUser().id === void 0) {
      return
    }

    notificationService.globalLoader(false)
    userService.globalLoader(false)
    axiosService.globalLoader(false)
    messageThreadService.globalLoader(false)

    const accountChain = userService.getById(context.mimicOrAuthenticatedUser().id).then(async (user: UserDto | null) => {
      if (user?.isLocked) {
        return host.$router.push('/logout')
      }

      if (!user) {
        return
      }

      void authenticationService().setUser(user)
      const messageCount = await messageThreadService.getUnreadThreadCount()
      void useUi().ui.setUnreadMessageCount(messageCount)

      const stats = await userService.getUserStats()
      await useUi().ui.setUserStats(stats)

      const sendHelpHeroData = context.shouldSendHelpHeroData ?? shouldSendHelpHeroData

      if (sendHelpHeroData(stats) && shell.isHelpHeroEnabled(context.authenticatedUser())) {
        HelpHero.identify(context.mimicOrAuthenticatedUser().id, {
          name: context.mimicOrAuthenticatedUser().name,
          email: context.mimicOrAuthenticatedUser().emailAddress,
          ...shell.statsForHelpHero(stats)
        })
      }

      const unreadNotifications = await notificationService.getUnreadNotificationCount()
      void useUi().ui.setUnreadNotificationCount(unreadNotifications)
    }).catch((e: unknown) => {
      // AuthExpiredDialog handles the UX for invalidated tokens — don't escalate to an unhandled
      // rejection here.
      if (authStoreGetters.isAuthExpired) {
        return
      }
      throw e
    }).finally(() => {
      notificationService.globalLoader(true)
      userService.globalLoader(true)
      messageThreadService.globalLoader(true)
    })

    /**
     * NOTE, carried over: the axios loader is only switched back on inside this branch, so on the mobile
     * build — where the liveness check never runs — it stays off for the rest of the session.
     */
    if (context.isMobileApp()) {
      return { account: accountChain, liveness: void 0 }
    }

    const livenessChain = axiosService.axios().post('auth/live', {}, {
      timeout: 5000
    }).then((res: { data: { version: string } }) => {
      if (shell.isVersionMismatch(res.data.version)) {
        host.$q.dialog({
          component: host.$bs.confirmDialog,
          // Quasar 2 passes a dialog component its props through `componentProps`; at the
          // top level they are dialog options and never reach the component.
          componentProps: {
            heading: 'Application Updated',
            bodyHtml: 'This application has been updated, please confirm to reload the page. If you have ' +
            'any unsaved changes, please select "No", save your changes then reload manually.'
          }
        }).onOk(() => {
          document.location.reload()
        })
      }
    }).catch((e: { code?: string }) => {
      if (shell.isTimeoutError(e)) {
        // nothing can be done about it, and Sentry does not need it
        console.log(e)
      } else {
        throw e
      }
    }).finally(() => {
      axiosService.globalLoader(true)
    })

    return { account: accountChain, liveness: livenessChain }
  }

  /** The app's own spinner, everywhere Quasar shows one. */
  const configureLoading = () => {
    Loading.setDefaults({
      // Quasar types `spinner` as a component definition, which this is.
      spinner: BsSpinner
    })
  }

  const configureCalendar = () => {
    host.$q.iconSet.datetime.arrowLeft = 'app:calendar-prev'
    host.$q.iconSet.datetime.arrowRight = 'app:calendar-next'
  }

  /**
   * A finished tour is recorded on the account so the visitor is not sent through it again.
   */
  const onChecklistCompleted = (event: ChecklistEvent, info: ChecklistEventInfo) => {
    return userService.update({
      id: context.mimicOrAuthenticatedUser().id,
      completedTours: shell.completedToursWith(
        context.mimicOrAuthenticatedUser().completedTours,
        info.checklist.name
      )
    }).then((user: UserDto) => {
      return authenticationService().setUser(user)
    })
  }

  /** Quasar's icon resolver over the app sprite. Was `created()` on BaseApp. */
  const configureIcons = () => {
    host.$q.iconMapFn = shell.iconMapFn(APP_ICONS, 'svguse:/cdn/app-icon-sprite.svg')
  }

  const startHelpHero = (handler: (event: ChecklistEvent, info: ChecklistEventInfo) => unknown) => {
    if (!shell.isHelpHeroEnabled(context.authenticatedUser())) {
      return
    }

    HelpHero = initHelpHero('rJcQnSjtzVY')
    HelpHero.on('checklist_completed', handler)
  }

  const stopHelpHero = (handler: (event: ChecklistEvent, info: ChecklistEventInfo) => unknown) => {
    if (!shell.isHelpHeroEnabled(context.authenticatedUser())) {
      return
    }

    HelpHero.off('checklist_completed', handler)
  }

  /**
   * One poll immediately, then once a minute — except under test, where a single call is enough and a
   * hanging api request would fail an unrelated test.
   */
  const startPolling = (routines: () => void) => {
    routines()

    if (shell.shouldRunIntervalRoutines()) {
      loggedInUserActionsInterval = setInterval(routines, 60000)
    }
  }

  const stopPolling = () => {
    clearInterval(loggedInUserActionsInterval)
  }

  /**
   * First Promoter attribution arrives as `?ref=` on any route. Read on update rather than on mount
   * because the router is not ready immediately. Was `updated()` on BaseApp.
   */
  const captureReferral = () => {
    if (host.$route.query?.ref) {
      LocalStorage.set('fp_ref', host.$route.query.ref)
    }
  }

  return {
    messageThreadService,
    authService,
    userService,
    notificationService,
    axiosService,
    shouldSendHelpHeroData,
    intervalRoutines,
    configureLoading,
    configureCalendar,
    configureIcons,
    onChecklistCompleted,
    startHelpHero,
    stopHelpHero,
    startPolling,
    stopPolling,
    captureReferral
  }
}
